import { createContext, useContext, useState } from 'react';

const ParameterBuilderContext = createContext();

export const useParameterBuilder = () => {
  return useContext(ParameterBuilderContext);
};

export const ParameterBuilderProvider = ({ children }) => {
  // Addition Prompt states
  const [styles, setStyles] = useState([]);
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');
  const [copySpace, setCopySpace] = useState(false);
  const [whiteBg, setWhiteBg] = useState(false);

  // Parameter states
  const [aspectRatios, setAspectRatios] = useState([]);
  const [stylizeValues, setStylizeValues] = useState([]);
  const [chaosValues, setChaosValues] = useState([]);
  const [quality, setQuality] = useState('High');
  const [version, setVersion] = useState('6.1');
  const [noValue, setNoValue] = useState('');
  const [personalization, setPersonalization] = useState('');
  const [additionalParams, setAdditionalParams] = useState('');

  // ฟังก์ชันสุ่มค่าจาก array
  const getRandomValue = (array) => {
    if (!array || array.length === 0) return '';
    return array[Math.floor(Math.random() * array.length)];
  };

  // ฟังก์ชันสุ่มเลือกค่าจาก array
  const getRandomItems = (items, count = 1) => {
    const shuffled = [...items].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // ฟังก์ชันสร้าง Addition Prompt
  const buildAdditionPrompt = (basePrompt) => {
    let additions = [];

    // เพิ่ม prefix ถ้ามี (ใส่ก่อน base prompt)
    if (prefix) {
      additions.push(prefix);
    }

    // เพิ่ม base prompt
    additions.push(basePrompt);

    // เพิ่ม style ที่สุ่มได้
    if (styles.length > 0) {
      additions.push(getRandomValue(styles));
    }

    // เพิ่ม copy space flag
    if (copySpace) {
      additions.push(', copy space for text');
    }

    // เพิ่ม white background flag
    if (whiteBg) {
      additions.push('isolate on white background');
    }

    // รวม additions ทั้งหมดด้วย space
    let result = additions.join(' ');

    // เพิ่ม suffix ถ้ามี (ใส่หลังสุดของส่วน prompt)
    if (suffix) {
      result += ` ${suffix}`;
    }

    return result;
  };

  // ฟังก์ชันสร้าง Parameter string
  const buildParameterString = (promptWithAdditions) => {
    let parameters = [];

    // เริ่มด้วย prompt ที่รวม additions แล้ว
    parameters.push(promptWithAdditions);

    // เพิ่ม aspect ratio ที่สุ่มได้
    if (aspectRatios.length > 0) {
      // รวม custom aspect ratios เข้าไปด้วย
      const allAspectRatios = [...aspectRatios];
      parameters.push(`--ar ${getRandomValue(allAspectRatios)}`);
    }

    // เพิ่ม stylize value ที่สุ่มได้
    if (stylizeValues.length > 0) {
      parameters.push(`--s ${getRandomValue(stylizeValues)}`);
    }

    // เพิ่ม chaos value ที่สุ่มได้
    if (chaosValues.length > 0) {
      parameters.push(`--c ${getRandomValue(chaosValues)}`);
    }

    // เพิ่ม quality
    if (quality) {
      parameters.push(`--q ${quality.toLowerCase() === 'high' ? '2' : '1'}`);
    }

    // เพิ่ม version
    if (version) {
      parameters.push(`--v ${version}`);
    }

    // เพิ่ม additional parameters
    if (additionalParams) {
      parameters.push(additionalParams);
    }

    // เพิ่ม no value
    if (noValue) {
      parameters.push(`--no ${noValue}`);
    }

    // เพิ่ม personalization ถ้ามี
    if (personalization) {
      parameters.push(personalization);
    }

    // รวม parameters ทั้งหมดด้วย space
    return parameters.join(' ');
  };

  // ฟังก์ชันสร้าง final prompts ทั้งหมด
  const buildFinalPrompts = (prompts, parameters) => {
    const {
      prefix,
      suffix,
      additionalParams,
      no,
      personalization,
      styles,
      aspectRatios,
      stylize,
      chaos,
      quality,
      version,
      copySpace,
      whiteBackground
    } = parameters;

    return prompts.map(basePrompt => {
      // แทนที่ comma ด้วย space ใน basePrompt
      let finalPrompt = basePrompt.replace(/,/g, '');

      // 1. เพิ่ม prefix
      if (prefix?.trim()) {
        finalPrompt = `${prefix.trim()} ${finalPrompt}`;
      }

      // 2. เพิ่ม style
      if (styles && styles.length > 0) {
        const randomStyle = getRandomItems(styles, 1)[0];
        if (randomStyle === 'realistic') {
          finalPrompt += ', Realistic Photo';
        } else {
          finalPrompt += `, ${randomStyle}`;
        }
      }

      // 3. เพิ่ม suffix
      if (suffix?.trim()) {
        finalPrompt += `, ${suffix.trim()}`;
      }

      // 4. เพิ่ม copy space
      if (copySpace) {
        finalPrompt += ', copy space for text';
      }

      // 5. เพิ่ม white background
      if (whiteBackground) {
        finalPrompt += ', isolate on white background';
      }

      // 6. เพิ่ม stylize (--s)
      if (stylize && stylize.length > 0) {
        const randomStylize = getRandomItems(stylize, 1)[0];
        finalPrompt += ` --s ${randomStylize}`;
      }

      // 7. เพิ่ม chaos (--c)
      if (chaos && chaos.length > 0) {
        const randomChaos = getRandomItems(chaos, 1)[0];
        finalPrompt += ` --c ${randomChaos}`;
      }

      // 8. เพิ่ม no (--no)
      if (no?.trim()) {
        finalPrompt += ` --no ${no.trim()}`;
      }

      // 9. เพิ่ม aspect ratio (--ar)
      if (aspectRatios && aspectRatios.length > 0) {
        const randomAspect = getRandomItems(aspectRatios, 1)[0];
        finalPrompt += ` --ar ${randomAspect}`;
      }

      // 10. เพิ่ม version (--v)
      if (version && version.toLowerCase() !== 'none') {
        finalPrompt += ` --v ${version}`;
      }

      // 11. เพิ่ม quality (--q)
      if (quality && quality.toLowerCase() !== 'none') {
        finalPrompt += ` --q ${quality.toLowerCase() === 'high' ? '2' : '1'}`;
      }

      // 12. เพิ่ม personalization (สุ่มเลือก 1 ค่า)
      if (personalization?.trim()) {
        // แยกค่า personalization ที่แยกด้วยบรรทัดใหม่
        const personalizationOptions = personalization.trim().split(/\s*\n\s*/).filter(p => p.trim());
        if (personalizationOptions.length > 0) {
          // สุ่มเลือก 1 ค่าจากตัวเลือกทั้งหมด
          const randomPersonalization = personalizationOptions[Math.floor(Math.random() * personalizationOptions.length)];
          finalPrompt += ` ${randomPersonalization}`;
        }
      }

      // 13. เพิ่ม additional parameters
      if (additionalParams?.trim()) {
        finalPrompt += ` ${additionalParams.trim()}`;
      }

      return finalPrompt;
    });
  };

  const value = {
    // Addition Prompt setters
    setStyles,
    setPrefix,
    setSuffix,
    setCopySpace,
    setWhiteBg,

    // Parameter setters
    setAspectRatios,
    setStylizeValues,
    setChaosValues,
    setQuality,
    setVersion,
    setNoValue,
    setPersonalization,
    setAdditionalParams,

    // Main functions
    buildAdditionPrompt,
    buildParameterString,
    buildFinalPrompts,
  };

  return (
    <ParameterBuilderContext.Provider value={value}>
      {children}
    </ParameterBuilderContext.Provider>
  );
};
